// =============================================================================
// src/data/identity-client.ts — Identidade do tenant (dono) do backend AtalaIA.
// Lê app_client (nome) + monitoring.monitored_actor (partido do ator "self") via
// Hasura anon (filtro multi-tenant por x-hasura-client-id). Fecha o handoff: a
// persona do observatório deixa de ser hardcoded (a vereadora fictícia) e passa a
// vir do tenant real (Omar Aziz, PSD). Espelha o padrão de insights-client.ts.
// =============================================================================

import type { OwnerIdentityLive } from '../app/owner-identity';

interface AppClientRow {
  readonly name: string;
  readonly slug: string;
  readonly entity_type: string;
}

interface MonitoredActorRow {
  readonly party_code: string | null;
}

interface IdentityGraphqlResponse {
  readonly data?: {
    readonly app_client?: readonly AppClientRow[];
    readonly monitoring_monitored_actor?: readonly MonitoredActorRow[];
  };
  readonly errors?: readonly unknown[];
}

const DEFAULT_TIMEOUT_MS = 8000;

const OWNER_IDENTITY_QUERY = `query OwnerIdentity {
  app_client { name slug entity_type }
  monitoring_monitored_actor(where: { actor_role: { _eq: "self" } }, limit: 1) { party_code }
}`;

/**
 * Busca a identidade do tenant (app_client.name + partido do ator "self") no Hasura
 * anon, filtrado por client_id (header). Retorna null em QUALQUER falha/vazio → o
 * chamador mantém o fallback estático (OWNER_IDENTITY_FALLBACK). Espelha fetchSectionInsights.
 *
 * @example
 *   const live = await fetchOwnerIdentity(HASURA_URL, clientId);
 *   if (live) setOwnerIdentity(composeOwnerIdentity(live));
 */
export async function fetchOwnerIdentity(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<OwnerIdentityLive | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-hasura-client-id': clientId },
      body: JSON.stringify({ query: OWNER_IDENTITY_QUERY }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const json = (await res.json()) as IdentityGraphqlResponse;
    const client = json.errors ? undefined : json.data?.app_client?.[0];
    if (!client) return null;
    return { name: client.name, party: json.data?.monitoring_monitored_actor?.[0]?.party_code ?? null };
  } catch {
    return null;
    /* v8 ignore next -- artefato v8-to-istanbul no `}` de fechamento do catch (catch só faz return null, nunca lança); cf. insights-client.ts a 100% */
  } finally {
    clearTimeout(timer);
  }
}
